// D:\directiveai-react\agthealth\agthealth-frontend\src\components\Login\views\LoginView.js

import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  InputAdornment, 
  IconButton, 
  Divider,
  Alert,
  Snackbar 
} from '@mui/material';
import { motion } from 'framer-motion';
import { 
  Eye, 
  EyeOff, 
  Lock, 
  ScanFace, 
  Stethoscope, 
  ChevronRight 
} from 'lucide-react';
import { 
  AuthCard, 
  StyledTextField, 
  FaceIDButton, 
  OrDivider, 
  ActionButton, 
  FlagContainer 
} from '../styles/StyledComponents';
import { detectIDType } from '../utils/validators';
import { HEALTH_GREEN } from '../utils/mockData';
import AppleSpinner from '../../../common/AppleSpinner';
import agthealthService from '../../../services/agthealthService';
import UserTypeSelector from '../components/UserTypeSelector';

const LoginView = ({ 
  onStartRegister, 
  onForgotPassword, 
  isMobile,
  onLoginSuccess,
  userType,
  onUserTypeChange
}) => {
  const [userId, setUserId] = useState('');
  const [userIdError, setUserIdError] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [detectedCountry, setDetectedCountry] = useState(null);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'error' });

  // Handle user ID change for login
  const handleUserIdChange = (e) => {
    const value = e.target.value;
    setUserId(value);
    
    // Si no hay valor, reset
    if (!value) {
      setUserIdError('');
      setDetectedCountry(null);
      return;
    }
    
    const idType = detectIDType(value);
    
    if (idType === 'email') {
      setUserIdError('');
      setDetectedCountry(null); // No mostrar bandera para email
    } else if (idType === 'cl' || idType === 'us') {
      setDetectedCountry(idType);
      setUserIdError('');
    } else {
      setUserIdError('Formato inválido. Ingrese email, RUT (12345678-9) o SSN (123-45-6789)');
      setDetectedCountry(null);
    }
  };

  // Login handler
  const handleLogin = async () => {
    console.log("Iniciando proceso de login");
    
    if (!userId || !password || !!userIdError) {
      console.log("Validación fallida:", { 
        userId: !!userId, 
        password: !!password, 
        hasError: !!userIdError 
      });
      
      setAlert({
        open: true,
        message: 'Por favor, completa todos los campos correctamente',
        severity: 'error'
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Determine login type (email or identification number)
      const idType = detectIDType(userId);
      console.log("Tipo de identificación:", idType);
      
      // Crear objeto de credenciales según el tipo de entrada
      const credentials = {
        password
      };

      // Añadir el campo correcto según el tipo de entrada
      if (idType === 'email') {
        credentials.email = userId;
      } else {
        credentials.identificationNumber = userId;
      }
      
      console.log("Enviando credenciales al servidor:", JSON.stringify(credentials));
      
      const response = await agthealthService.login(credentials);
      console.log("Respuesta del servidor:", response);
      
      setAlert({
        open: true,
        message: 'Inicio de sesión exitoso',
        severity: 'success'
      });
      
      // Notify parent component of successful login
      if (onLoginSuccess) {
        console.log("Notificando inicio de sesión exitoso");
        onLoginSuccess(response.data.user);
      }
    } catch (error) {
      console.error('Error detallado en login:', error);
      console.error('Respuesta del servidor:', error.response?.data);
      console.error('Estado HTTP:', error.response?.status);
      
      setAlert({
        open: true,
        message: error.response?.data?.message || 'Error al iniciar sesión',
        severity: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Face ID login handler
  const handleFaceIDLogin = () => {
    setIsSubmitting(true);
    // Simulate Face ID authentication
    setTimeout(() => {
      setIsSubmitting(false);
      setAlert({
        open: true,
        message: 'Face ID no disponible en este momento',
        severity: 'info'
      });
    }, 2000);
  };

  const handleCloseAlert = () => {
    setAlert({ ...alert, open: false });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      style={{ width: '100%' }}
    >
      <Snackbar 
        open={alert.open} 
        autoHideDuration={6000} 
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity={alert.severity} sx={{ width: '100%' }}>
          {alert.message}
        </Alert>
      </Snackbar>
      
      <AuthCard>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          mb: isMobile ? 2 : 3, 
          alignItems: 'center'
        }}>
          <Typography 
            variant={isMobile ? "h5" : "h4"} 
            sx={{ 
              fontWeight: 600, 
              mb: 1, 
              display: 'flex', 
              alignItems: 'center' 
            }}
          >
            AGT Health
            <motion.span
              initial={{ scale: 1 }}
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 1, repeat: Infinity, repeatDelay: 5 }}
              style={{ 
                color: HEALTH_GREEN, 
                fontSize: isMobile ? '24px' : '32px', 
                fontWeight: 700, 
                marginLeft: '2px',
                position: 'relative',
                top: '-8px'
              }}
            >
              +
            </motion.span>
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Stethoscope size={isMobile ? 18 : 20} color={HEALTH_GREEN} />
            <Typography variant="subtitle1" color="text.secondary">
              Healthcare Professional Portal
            </Typography>
          </Box>
        </Box>

        <UserTypeSelector 
          userType={userType} 
          onUserTypeChange={onUserTypeChange} 
        />

        <StyledTextField
          fullWidth
          label="Professional ID or Email"
          variant="outlined"
          value={userId}
          onChange={handleUserIdChange}
          placeholder="RUT, SSN or Email"
          error={!!userIdError}
          helperText={userIdError}
          InputProps={{
            startAdornment: detectedCountry && (
              <InputAdornment position="start">
                <FlagContainer sx={{ width: 24, height: 16, marginRight: 0 }}>
                  <Box 
                    component="img" 
                    src={`https://flagcdn.com/w40/${detectedCountry}.png`}
                    alt={`${detectedCountry === 'cl' ? 'Chile' : 'US'} flag`}
                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </FlagContainer>
              </InputAdornment>
            ),
          }}
        />
        
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, mb: 1, display: 'block' }}>
          Puedes iniciar sesión con tu email, RUT o SSN.
        </Typography>

        <StyledTextField
          fullWidth
          label="Password"
          variant="outlined"
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Lock size={18} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              handleLogin();
            }
          }}
        />

        <Typography 
          variant="body2" 
          color={HEALTH_GREEN} 
          sx={{ 
            textAlign: 'right', 
            mb: 1, 
            cursor: 'pointer',
            '&:hover': {
              textDecoration: 'underline',
            }
          }}
          onClick={onForgotPassword}
        >
          Forgot Password?
        </Typography>

        <ActionButton
          variant="contained"
          disableElevation
          fullWidth
          disabled={isSubmitting || !userId || !password || !!userIdError}
          onClick={handleLogin}
          endIcon={<ChevronRight size={16} />}
        >
          {isSubmitting ? (
            <div style={{ transform: 'translateY(-5px)' }}>
              <AppleSpinner sizeDesktop={24} sizeMobile={20} />
            </div>
          ) : (
            'Sign In'
          )}
        </ActionButton>

        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Typography variant="body2" color="text.secondary">
            Don't have an account?{' '}
            <Typography 
              component="span" 
              variant="body2"
              color={HEALTH_GREEN} 
              sx={{ 
                fontWeight: 500, 
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' }
              }}
              onClick={onStartRegister}
            >
              Create Account
            </Typography>
          </Typography>
        </Box>
      </AuthCard>
    </motion.div>
  );
};

export default LoginView;